import { createContext, useContext, type ReactElement } from 'react';
import type { AutoLinkState } from './auto-link-types.js';

export type AutoLinkBootstrapRequest = {
  readonly accountId: string;
  readonly signal: AbortSignal;
};

export type AutoLinkBootstrapResult =
  | { readonly kind: 'settled'; readonly state: AutoLinkState }
  | { readonly kind: 'stale' };

/**
 * Where this account's zero-Studio run is, for a bootstrap pass deciding
 * whether to join it. The guard wraps only Root's index route, so this lives
 * until Root navigates away.
 * - `none`: no run, or the run ended without committing a link.
 * - `checking`: in flight, nothing linked yet (its Studio create may be in flight).
 * - `linking`: in flight, linking or verifying the Bridge.
 * - `succeeded`: the run linked and verified the Bridge. Any other decision
 *   comes from a read that has not caught up with it (inventory or identity).
 * - `link_committed`: the run committed the link, then failed (for example
 *   verification timed out). The Bridge is still this session's, never foreign.
 */
export type ZeroRoomAutoLinkRunPhase =
  | 'none'
  | 'checking'
  | 'linking'
  | 'succeeded'
  | 'link_committed';

export type AutoLinkBootstrapControl = {
  /**
   * Starts the account's zero-Studio run, or joins the one already started.
   * Aborting `request.signal` only detaches this caller; it never cancels the run.
   */
  readonly requestZeroRoomAutoLink: (
    request: AutoLinkBootstrapRequest
  ) => Promise<AutoLinkBootstrapResult>;
  readonly completeWithoutAutoLink: (request: AutoLinkBootstrapRequest) => void;
  readonly getZeroRoomAutoLinkRunPhase: (accountId: string) => ZeroRoomAutoLinkRunPhase;
};

const Context = createContext<AutoLinkBootstrapControl | undefined>(undefined);

class MissingAutoLinkBootstrapProviderError extends Error {
  constructor() {
    super('Auto-link bootstrap control is unavailable outside AutoLinkGuard.');
    this.name = 'MissingAutoLinkBootstrapProviderError';
  }
}

export const AutoLinkBootstrapProvider = ({
  children,
  value
}: {
  readonly children: ReactElement;
  readonly value: AutoLinkBootstrapControl;
}): ReactElement => <Context.Provider value={value}>{children}</Context.Provider>;

export const useAutoLinkBootstrap = (): AutoLinkBootstrapControl => {
  const value = useContext(Context);
  if (value === undefined) {
    throw new MissingAutoLinkBootstrapProviderError();
  }
  return value;
};
